/* eslint-disable no-prototype-builtins */
/**
 * Helper services to support the modifications to config on init.
 * Methods not directly related to the get and set of behaviour subjects or fetching of api data.
 */
import { Injectable } from '@angular/core';
import { IProductInfo } from '../product/models/product-info.model';
import { IProductPackageItem } from '../product/models/product-package-item.model';
import { CDateBasedProductTypes, CSessionBasedProductTypes, TProductType } from '../product/models/product-type.type';
import { IProduct } from '../product/models/product.model';
import { IRestrictedAddons } from '../product/models/restricted-addons.model';
import { ICheckoutProductCategory } from './models/checkout-product-category.model';
import { ICheckoutProduct } from './models/checkout-product.model';
import { IVenueAddon } from './models/venue-addons.model';
import { IVenueConfig } from './models/venue-config.model';
import { IVenueHomeProductWidget } from './models/venue-home-product-widget.model';
import { EPricingRuleType, ICalcProductPricingRule } from '@roller/booking-calculation-service';
import { IProductPackageOptionSet } from '../product/models/product-option-set.model';

@Injectable({
  providedIn: 'root'
})
export class VenueConfigHelperService {
  /** Product problem, but occasionally addon's are passed into products with no tickets. */
  filterProductsWithNoTickets(products: IProduct[]): IProduct[] {
    return products.filter((prod: IProduct) => {
      return !!prod.products && !!prod.products.length;
    });
  }

  /** Due to a random bug in BE occasionally duplications of addonIds are being sent - creates a new set. */
  filterUniqueAddons(products: IProduct[]): IProduct[] {
    return products.map((product: IProduct) => {
      product.addOns = product.addOns?.length ? [...new Set(product.addOns)] : [];
      return product;
    });
  }

  /**
   * If the API populates products with the default blank placeholder then set the image to be undefined
   * !NOTE This is temp (Nov 2023) until the BE gets capacity to clean up the endpoint correctly.
   */
  removeBlankImagesFromProducts(venueConfig: IVenueConfig, products: IProduct[], defaultImageUrl?: string): IProduct[] {
    if (!defaultImageUrl || venueConfig.checkoutType !== 'foodAndBeverageCheckout') {
      return products;
    }

    return products.map((product: IProduct) => {
      if (product.imageUrl?.includes(defaultImageUrl)) {
        product.imageUrl = undefined;
      }

      product.products = product.products.map((productInfo: IProductInfo) => {
        if (productInfo.imageUrl?.includes(defaultImageUrl)) {
          productInfo.imageUrl = undefined;
        }

        return productInfo;
      });
      return product;
    });
  }

  /** Add party package specific details to packageItems */
  addProductsToPartyPackageItems(products: IProduct[]): IProduct[] {
    return products.map((product: IProduct) => {
      if (product.type !== 'partypackage') {
        return product;
      }

      product.products.forEach((ticket: IProductInfo) => {
        // only guest variations
        ticket.packageItems = ticket.packageItems?.filter((pItem: IProductPackageItem) => {
          return pItem.productId === pItem.parentProductId;
        });
        ticket.packageItems?.forEach((item: IProductPackageItem) => {
          // Guest can only select one option when there is only one quantity per package
          item.isSingleSelection = item.quantity === 1 && item.quantityType === 'package';

          // Add product name and variations to each package items
          const packageItemDetail = products.find((product: IProduct) => product.id === item.productId);
          if (packageItemDetail) {
            item.name = packageItemDetail.name;
            item.variations = structuredClone(packageItemDetail.products);
          }
        });
      });

      return product;
    });
  }

  /** Add product specific details to productOptionSets */
  addProductsToProductOptionSets(products: IProduct[]): IProduct[] {
    return products.map((product: IProduct) => {
      // Option Sets only apply to packages currently, may be removed in the future
      if (product.type !== 'package') {
        return product;
      }

      product.products.forEach((ticket: IProductInfo) => {
        ticket.packageOptionSets?.forEach((item: IProductPackageOptionSet) => {
          // Add variations to each option set
          item.variations = products
            .flatMap((x) => x.products)
            .filter((productInfo: IProductInfo) => item.productIds.includes(productInfo.id))
            .map((productInfo: IProductInfo) => structuredClone(productInfo));
        });
      });

      return product;
    });
  }

  /** Adds product and parentProductId to tickets and other useful properties that always remain constant */
  addProductTypesToTickets(products: IProduct[]): IProduct[] {
    return products.map((product: IProduct) => {
      product.isItemDateBased = this.isItemDateBased(product);
      product.isItemSessionBased = this.isItemSessionBased(product);
      product.products = product.products.map((ticket: IProductInfo) => {
        return {
          ...ticket,
          isItemDateBased: product.isItemDateBased,
          isItemSessionBased: product.isItemSessionBased,
          type: product.type,
          parentProductId: product.id,
          isWaiverRequired: product.isWaiverRequired,
          modifiers: product.modifiers,
          agreement: product?.agreement
        };
      });

      return product;
    });
  }

  /**
   * Evaluates a ticket or product to determine if the item is date based.
   * Shows date selection menu or used in isSameDateCalculations.
   * Product types that are date based:
   * * sessionPass - always.
   * * pass - sometimes (if it does not have valid days or validDaysFromRedemption).
   * * packages - always
   */
  private isItemDateBased(ticket: IProductInfo | IProduct | undefined): boolean {
    if (!ticket) {
      return false;
    }

    if (CDateBasedProductTypes.includes(ticket.type as TProductType)) {
      return true;
    }

    const validDays = ticket.validDays || 0;
    const validDaysFromRedemption = ticket.validDaysFromRedemption || 0;
    if (ticket.type === 'pass' && !(validDays > 1 || validDaysFromRedemption > 1)) {
      return true;
    }

    return false;
  }

  /**
   * Evaluates a ticket or product to determine if the item is session based.
   * Shows session selection menu or used in isSameSessionCalculations.
   * Session based item are either:
   * * sessionPass or
   * * packages that contain session passes
   */
  private isItemSessionBased(ticket: IProductInfo | IProduct): boolean {
    if (CSessionBasedProductTypes.includes(ticket.type as TProductType)) {
      return true;
    }

    if (
      ticket.type === 'package' &&
      (ticket.packageSubType?.includes('sessionpass') || ticket.packageSubType?.includes('recurringpass'))
    ) {
      return true;
    }

    return false;
  }

  /**
   * A package has a list of products that have packageItems. Finds parent product to group parent product types.
   * Adds an array of strings of type TProductType to the packageSubType.
   * Adds validDays and validDaysFromRedemption to the package from the packageItem. Note this is only for determining to show the date selector.
   * Increase min/maxPurchase for each ticket based on their contained packageItem matching ticket min/maxPurchase value
   * Sets isWaiverRequired for package tickets by contained products (e.g. package session requires waiver)
   */
  mapPackageProducts(products: IProduct[]): IProduct[] {
    const hideDurationProducts = products.filter(
      (product: IProduct) => product.type === 'sessionpass' && !!product.hideSessionDuration
    );

    return products.map((product: IProduct) => {
      if (product.type !== 'package') {
        return product;
      }

      product.packageSubType = [];

      product.products.forEach((pInfo: IProductInfo) => {
        pInfo.packageItems?.forEach((packageItem: IProductPackageItem) => {
          const packageItemParentProduct: IProduct | undefined = products.find(
            (fProduct: IProduct) => fProduct.id === packageItem.parentProductId
          );

          if (!product.hideSessionDuration) {
            product.hideSessionDuration = !!hideDurationProducts.find(
              (hideDurationProduct: IProduct) => hideDurationProduct.id === packageItem.parentProductId
            );
          }

          if (packageItemParentProduct) {
            if (!product.validDays && !!packageItemParentProduct.validDays) {
              product.validDays = packageItemParentProduct.validDays;
            }

            if (!product.validDaysFromRedemption && !!packageItemParentProduct.validDaysFromRedemption) {
              product.validDaysFromRedemption = packageItemParentProduct.validDays;
            }

            if (!product.packageSubType?.includes(packageItemParentProduct.type as TProductType)) {
              product.packageSubType?.push(packageItemParentProduct?.type as TProductType);
            }

            const packageItemTicket: IProductInfo | undefined = packageItemParentProduct.products.find(
              (parentTickets: IProductInfo) => parentTickets.id === packageItem.productId
            );

            if (!!packageItemTicket && packageItemTicket.minPurchase > pInfo.minPurchase) {
              pInfo.minPurchase = packageItemTicket.minPurchase;
            }

            packageItem.tax = packageItemTicket?.tax;
            packageItem.isTaxInclusive = packageItemTicket?.isTaxInclusive;
            pInfo.isWaiverRequired = pInfo.isWaiverRequired || packageItemParentProduct.isWaiverRequired;

            // show remaining resources if at least one of the package items in variations has the corresponded flag enabled
            if (
              !product.displayRemainingResourcesInsteadOfTickets &&
              packageItemParentProduct.displayRemainingResourcesInsteadOfTickets
            ) {
              product.displayRemainingResourcesInsteadOfTickets =
                packageItemParentProduct.displayRemainingResourcesInsteadOfTickets;
            }
          }
        });
      });

      if (product.packageSubType.length) {
        product.products = product.products.map((info: IProductInfo) => {
          return {
            ...info,
            packageSubType: product.packageSubType,
            validDays: product.validDays,
            validDaysFromRedemption: product.validDaysFromRedemption
          };
        });
      }

      return product;
    });
  }

  /**
   * Helper function that removes the incorrect maxPurchase value from wallet tickets and assigns it to the guest count property instead
   * The maxPurchase is not the max purchase for a wallet item. Wallets have no maxPurchase which is set by a customer, and
   * It's actually a shared wallet guest limit which should not affect the purchase limit (same in legacy)
   * This prevents us from having to add mutations in availability calculations (and more) for this specific product type.
   */
  mapWalletProducts(products: IProduct[]): IProduct[] {
    return products.map((product: IProduct) => {
      if (product.type !== 'wallet') {
        return product;
      }

      product.products.forEach((info: IProductInfo) => {
        if (info.maxPurchase) {
          info.sharedWalletGuestLimit = info.maxPurchase;
          delete info.maxPurchase;
        }
      });

      return product;
    });
  }

  mapPricingTimeRuleToTickets(parentProducts: IProduct[], pricingRules: ICalcProductPricingRule[]): IProduct[] {
    if (!pricingRules?.length) {
      return parentProducts;
    }

    return parentProducts.map((product: IProduct) => {
      const pricingRule = pricingRules.find(
        (rule: ICalcProductPricingRule) => rule.pricingRuleId === product.pricingRuleId
      );
      product.products.forEach((ticket: IProductInfo) => {
        if (!ticket.pricingRule || ticket.pricingRule.type !== EPricingRuleType.time || !pricingRule) {
          return;
        }

        ticket.pricingRule.timeRule = pricingRule.timeRule;
      });

      return product;
    });
  }

  /** Ensure that all addon id's are strings to match product Ids */
  venueAddonsMapToString(addOns: IVenueAddon[]): IVenueAddon[] {
    return addOns.map((venueAddon: IVenueAddon) => {
      venueAddon.id = venueAddon.id.toString();
      return venueAddon;
    });
  }

  /** Helper to add product Information to appropriate locations in the venue Configuration. */
  addProductsToProductPage(venueConfig: IVenueConfig, products: IProduct[]): IVenueConfig {
    venueConfig = this.ensureAddonIdsString(venueConfig);

    if (!!venueConfig.productsPage.productCategories && venueConfig.productsPage.productCategories.length > 0) {
      venueConfig.productsPage.productCategories.forEach((prodCat: ICheckoutProductCategory) => {
        const fullProducts: IProduct[] = [];

        if (!!prodCat.products && prodCat.products.length > 0) {
          prodCat.products.forEach((venueProd: ICheckoutProduct) => {
            const findProduct = products.find((product: IProduct) => product.id.toString() === venueProd.id.toString());
            if (findProduct) {
              this.populateAddonList(findProduct, products);
              fullProducts.push({ ...findProduct, order: venueProd.order });
            }
          });
        }

        prodCat.fullProducts = fullProducts.sort((a, b) => {
          if (typeof a.order === 'undefined') {
            return 1;
          }

          if (typeof b.order === 'undefined') {
            return -1;
          }

          return a.order - b.order;
        });
      });
    }

    return venueConfig;
  }

  /** Make sure the productsPage Addons have string id numbers. */
  private ensureAddonIdsString(venueConfig: IVenueConfig): IVenueConfig {
    if (!!venueConfig.productsPage.addOns && venueConfig.productsPage.addOns.length > 0) {
      venueConfig.productsPage.addOns = venueConfig.productsPage.addOns.map((venueAddon: IVenueAddon) => {
        return {
          ...venueAddon,
          id: venueAddon.id.toString()
        };
      });
    }

    return venueConfig;
  }

  /** Takes the list of addon product ID's and adds it to findProduct and returns. */
  private populateAddonList(findProduct: IProduct, products: IProduct[]): void {
    if (findProduct.addonList) {
      return;
    }

    findProduct.addonList = [];

    const hasAddOns = !!findProduct.addOns?.length;
    const hasRestrictedAddOns = !!findProduct.restrictedAddOns?.length;

    if (!hasAddOns && !hasRestrictedAddOns) {
      return;
    }

    findProduct.addOns.forEach((addon: string) => {
      const findAddon = products.find((product: IProduct) => product.id === addon);
      if (findAddon) {
        const freshAddon = structuredClone(findAddon);
        // because addons can be anything this means they can have their own addons which can create a circular loop
        if (freshAddon.hasOwnProperty('addonList')) {
          delete freshAddon.addonList;
        }

        findProduct.addonList?.push(freshAddon);
      }
    });

    findProduct.restrictedAddOns?.forEach((restrictedAddOn: IRestrictedAddons) => {
      restrictedAddOn.addOns.forEach((addonId: string) => {
        const findAddon = products.find((product: IProduct) => product.id === addonId);
        if (!findAddon) {
          return;
        }

        if (!findProduct.addonList?.find((addon: IProduct) => addon.id === findAddon.id)) {
          const freshAddon = structuredClone(findAddon);
          if (freshAddon.hasOwnProperty('addonList')) {
            delete freshAddon.addonList;
          }

          findProduct.addonList?.push(freshAddon);
        }
      });
    });
  }

  /** After the venueConfig has products page full products populated then only add to homePage  */
  addProductsToWidget(venueConfig: IVenueConfig, products: IProduct[]): IVenueConfig {
    if (venueConfig.homePage.isEnabled && !!venueConfig.homePage.widgets) {
      venueConfig.homePage.widgets?.forEach((widget: IVenueHomeProductWidget) => {
        widget.fullProducts = [];
        if (widget.type === 'product-carousel' && !!widget.productCategory?.products) {
          if (widget.productCategory.products.length > 0) {
            widget.fullProducts.push(...this.findWidgetProducts(widget.productCategory.products, products));
          }
        } else if (!!widget.products && widget.products.length > 0) {
          widget.fullProducts.push(...this.findWidgetProducts(widget.products, products));
        }
      });
    }

    return venueConfig;
  }

  /** Sorts according to order each product and collects found products to an array to be added to fullProducts.  */
  private findWidgetProducts(widgetProduct: ICheckoutProduct[], products: IProduct[] | undefined): IProduct[] {
    if (!products || !products.length) {
      return [];
    }

    const addProduct: IProduct[] = [];
    widgetProduct
      .sort((a, b) => a.order - b.order)
      .forEach((venueProd: ICheckoutProduct) => {
        const findProduct = products.find((product: IProduct) => product.id === venueProd.id.toString());
        if (findProduct) {
          this.populateAddonList(findProduct, products);
          addProduct.push({ ...findProduct, order: venueProd.order });
        }
      });

    return addProduct;
  }
}
